import axios from "axios";
import attachResponseInterceptor from "./TokenRefreshingInterceptor";
import { attachLoginSessionInterceptor } from "./LoginSession";

class AxiosFactory {
    /** @type {import("axios").CreateAxiosDefaults} */
    #defaultConfig;

    /**
     * Constructor for the `AxiosFactory`. It takes a default configuration for axios instances.
     *
     * @param {import("axios").CreateAxiosDefaults} defaultConfig
     */
    constructor(defaultConfig) {
        this.#defaultConfig = defaultConfig;
    }

    /**
     * Creates an axios instance.
     *
     * @note This function attaches interceptors to the axios instance.
     *
     * @param {string} endpointUrl Endpoint URL for the axios instance. This will be appended to the base URL
     * that was set in the default config (if a baseURL wasn't specified in the default config, then the full URL
     * must be passed to this function). If the URL doesn't start with `/`, then it will be added.
     *
     * @returns {import("axios").AxiosInstance} An axios instance.
     */
    create(endpointUrl = "") {
        const hasSlash = this.#defaultConfig?.baseURL.endsWith("/") || endpointUrl?.startsWith("/");
        const axiosInstance = axios.create({
            ...this.#defaultConfig,
            // Prepend a "/" to the endpoint URL if needed (don't prepend if there's no baseURL because if there's no baseURL the entire URL must be passed in)
            baseURL: `${this.#defaultConfig.baseURL ?? ""}${hasSlash ? endpointUrl : `/${endpointUrl}`}`,
        });

        attachResponseInterceptor(axiosInstance);
        attachLoginSessionInterceptor(axiosInstance);

        return axiosInstance;
    }
}

export default AxiosFactory;
